import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { ratesApi, roomsApi } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { Plus, DollarSign, Calendar, Percent, Edit, Trash2, Coffee, Ban, Clock } from 'lucide-react';

export default function RatesPage() {
  const { currentProperty } = useAuth();
  const [ratePlans, setRatePlans] = useState([]);
  const [restrictions, setRestrictions] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('plans');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [isRestrictionDialogOpen, setIsRestrictionDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!currentProperty) return;
    try {
      setLoading(true);
      const [plansRes, restrictionsRes, roomTypesRes] = await Promise.all([
        ratesApi.listPlans(currentProperty.id, { active_only: false }),
        ratesApi.listRestrictions(currentProperty.id),
        roomsApi.listTypes(currentProperty.id)
      ]);
      setRatePlans(plansRes.data);
      setRestrictions(restrictionsRes.data);
      setRoomTypes(roomTypesRes.data);
    } catch (error) {
      toast.error('Failed to load rate data');
    } finally {
      setLoading(false);
    }
  }, [currentProperty]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreatePlan = async (formData) => {
    try {
      await ratesApi.createPlan(currentProperty.id, formData);
      toast.success('Rate plan created successfully');
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create rate plan');
    }
  };

  const handleUpdatePlan = async (formData) => {
    try {
      await ratesApi.updatePlan(editingPlan.id, formData);
      toast.success('Rate plan updated successfully');
      setEditingPlan(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update rate plan');
    }
  };

  const handleCreateRestriction = async (formData) => {
    try {
      await ratesApi.createRestriction(currentProperty.id, formData);
      toast.success('Restriction created successfully');
      setIsRestrictionDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create restriction');
    }
  };

  const handleDeleteRestriction = async (id) => {
    try {
      await ratesApi.deleteRestriction(id);
      toast.success('Restriction deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete restriction');
    }
  };

  const getMealPlanLabel = (code) => {
    const labels = { RO: 'Room Only', BB: 'Bed & Breakfast', HB: 'Half Board', FB: 'Full Board', AI: 'All Inclusive' };
    return labels[code] || code;
  };

  const getRestrictionTypeLabel = (type) => {
    const labels = { STOP_SELL: 'Stop Sell', CTA: 'Closed to Arrival', CTD: 'Closed to Departure', MIN_LOS: 'Min Length of Stay', MAX_LOS: 'Max Length of Stay' };
    return labels[type] || type;
  };

  return (
    <div className="space-y-6" data-testid="rates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Rate Management</h1>
          <p className="text-muted-foreground">Configure rate plans and pricing for {currentProperty?.name}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="plans" data-testid="tab-plans">Rate Plans</TabsTrigger>
          <TabsTrigger value="restrictions" data-testid="tab-restrictions">Restrictions</TabsTrigger>
        </TabsList>

        <TabsContent value="plans" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-rate-plan-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Rate Plan
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create Rate Plan</DialogTitle>
                </DialogHeader>
                <RatePlanForm onSubmit={handleCreatePlan} onCancel={() => setIsDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Card key={i}><CardContent className="p-6"><Skeleton className="h-32" /></CardContent></Card>
              ))}
            </div>
          ) : ratePlans.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center" data-testid="empty-rate-plans">
                <DollarSign className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No Rate Plans</h3>
                <p className="text-muted-foreground mt-2">Create your first rate plan to start selling rooms.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ratePlans.map((plan) => (
                <Card key={plan.id} className="relative" data-testid={`rate-plan-${plan.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{plan.name}</CardTitle>
                        <CardDescription className="font-mono text-xs">{plan.code}</CardDescription>
                      </div>
                      <Badge variant={plan.status === 'ACTIVE' ? 'default' : 'secondary'}>
                        {plan.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold">{plan.currency} {plan.base_rate?.toLocaleString()}</span>
                        <Badge variant="outline">{plan.rate_type}</Badge>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {plan.includes_breakfast && (
                          <Badge variant="secondary" className="gap-1">
                            <Coffee className="h-3 w-3" /> Breakfast
                          </Badge>
                        )}
                        <Badge variant="secondary">{getMealPlanLabel(plan.meal_plan)}</Badge>
                      </div>
                      {plan.min_nights > 1 && (
                        <div className="text-sm text-muted-foreground">
                          Min {plan.min_nights} nights
                        </div>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-2"
                        onClick={() => setEditingPlan(plan)}
                        data-testid={`edit-plan-${plan.id}`}
                      >
                        <Edit className="h-4 w-4 mr-2" /> Edit Plan
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="restrictions" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={isRestrictionDialogOpen} onOpenChange={setIsRestrictionDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-restriction-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Restriction
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Restriction</DialogTitle>
                </DialogHeader>
                <RestrictionForm
                  roomTypes={roomTypes}
                  ratePlans={ratePlans}
                  onSubmit={handleCreateRestriction}
                  onCancel={() => setIsRestrictionDialogOpen(false)}
                />
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <Table data-testid="restrictions-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Room Type</TableHead>
                  <TableHead>Rate Plan</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 6 }).map((_, j) => (
                        <TableCell key={j}><Skeleton className="h-4" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : restrictions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No restrictions configured.
                    </TableCell>
                  </TableRow>
                ) : (
                  restrictions.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>
                        <Badge variant={r.restriction_type === 'STOP_SELL' ? 'destructive' : 'secondary'}>
                          {getRestrictionTypeLabel(r.restriction_type)}
                        </Badge>
                      </TableCell>
                      <TableCell>{r.room_types?.name || 'All'}</TableCell>
                      <TableCell>{r.rate_plans?.name || 'All'}</TableCell>
                      <TableCell>{r.start_date} to {r.end_date}</TableCell>
                      <TableCell>{r.value || '-'}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteRestriction(r.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Plan Dialog */}
      <Dialog open={!!editingPlan} onOpenChange={() => setEditingPlan(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Rate Plan</DialogTitle>
          </DialogHeader>
          {editingPlan && (
            <RatePlanForm
              plan={editingPlan}
              onSubmit={handleUpdatePlan}
              onCancel={() => setEditingPlan(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RatePlanForm({ plan, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    code: plan?.code || '',
    name: plan?.name || '',
    description: plan?.description || '',
    rate_type: plan?.rate_type || 'PUBLIC',
    base_rate: plan?.base_rate || '',
    currency: plan?.currency || 'ETB',
    includes_breakfast: plan?.includes_breakfast || false,
    includes_taxes: plan?.includes_taxes || false,
    meal_plan: plan?.meal_plan || 'RO',
    deposit_required: plan?.deposit_required || false,
    deposit_percentage: plan?.deposit_percentage || 0,
    min_nights: plan?.min_nights || 1,
    max_nights: plan?.max_nights || '',
    min_advance_days: plan?.min_advance_days || 0,
    status: plan?.status || 'ACTIVE',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleaned = { ...formData };
    // Convert numeric fields properly
    cleaned.base_rate = parseFloat(cleaned.base_rate) || 0;
    cleaned.min_nights = parseInt(cleaned.min_nights) || 1;
    cleaned.min_advance_days = parseInt(cleaned.min_advance_days) || 0;
    cleaned.deposit_percentage = parseFloat(cleaned.deposit_percentage) || 0;
    // Remove empty optional fields to avoid validation errors
    if (!cleaned.max_nights && cleaned.max_nights !== 0) {
      delete cleaned.max_nights;
    } else {
      cleaned.max_nights = parseInt(cleaned.max_nights);
    }
    if (!cleaned.description) delete cleaned.description;
    onSubmit(cleaned);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="rate-plan-form">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Code *</Label>
          <Input
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
            required
            disabled={!!plan}
            placeholder="e.g., BAR"
            data-testid="plan-code-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Name *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            placeholder="e.g., Best Available Rate"
            data-testid="plan-name-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Rate Type</Label>
          <Select value={formData.rate_type} onValueChange={(v) => setFormData({ ...formData, rate_type: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="PUBLIC">Public</SelectItem>
              <SelectItem value="CORPORATE">Corporate</SelectItem>
              <SelectItem value="PROMOTIONAL">Promotional</SelectItem>
              <SelectItem value="PACKAGE">Package</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Base Rate *</Label>
          <Input
            type="number"
            value={formData.base_rate}
            onChange={(e) => setFormData({ ...formData, base_rate: e.target.value })}
            required
            min="0"
            step="0.01"
            data-testid="plan-rate-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Currency</Label>
          <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ETB">ETB</SelectItem>
              <SelectItem value="USD">USD</SelectItem>
              <SelectItem value="EUR">EUR</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Meal Plan</Label>
          <Select value={formData.meal_plan} onValueChange={(v) => setFormData({ ...formData, meal_plan: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="RO">Room Only</SelectItem>
              <SelectItem value="BB">Bed & Breakfast</SelectItem>
              <SelectItem value="HB">Half Board</SelectItem>
              <SelectItem value="FB">Full Board</SelectItem>
              <SelectItem value="AI">All Inclusive</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Min Nights</Label>
          <Input
            type="number"
            value={formData.min_nights}
            onChange={(e) => setFormData({ ...formData, min_nights: parseInt(e.target.value) })}
            min="1"
          />
        </div>
      </div>

      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <Switch
            checked={formData.includes_breakfast}
            onCheckedChange={(v) => setFormData({ ...formData, includes_breakfast: v })}
          />
          <Label>Includes Breakfast</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={formData.includes_taxes}
            onCheckedChange={(v) => setFormData({ ...formData, includes_taxes: v })}
          />
          <Label>Taxes Included</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={formData.deposit_required}
            onCheckedChange={(v) => setFormData({ ...formData, deposit_required: v })}
          />
          <Label>Deposit Required</Label>
        </div>
      </div>

      {plan && (
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="INACTIVE">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" data-testid="plan-submit-btn">{plan ? 'Update' : 'Create'} Rate Plan</Button>
      </div>
    </form>
  );
}

function RestrictionForm({ roomTypes, ratePlans, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    room_type_id: '',
    rate_plan_id: '',
    restriction_type: 'STOP_SELL',
    start_date: '',
    end_date: '',
    value: '',
    notes: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = { ...formData };
    if (!data.room_type_id) delete data.room_type_id;
    if (!data.rate_plan_id) delete data.rate_plan_id;
    if (!data.value) delete data.value;
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="restriction-form">
      <div className="space-y-2">
        <Label>Restriction Type *</Label>
        <Select value={formData.restriction_type} onValueChange={(v) => setFormData({ ...formData, restriction_type: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="STOP_SELL">Stop Sell</SelectItem>
            <SelectItem value="CTA">Closed to Arrival</SelectItem>
            <SelectItem value="CTD">Closed to Departure</SelectItem>
            <SelectItem value="MIN_LOS">Min Length of Stay</SelectItem>
            <SelectItem value="MAX_LOS">Max Length of Stay</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Room Type (optional)</Label>
          <Select value={formData.room_type_id || 'all'} onValueChange={(v) => setFormData({ ...formData, room_type_id: v === 'all' ? '' : v })}>
            <SelectTrigger><SelectValue placeholder="All room types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All room types</SelectItem>
              {roomTypes.map((rt) => (
                <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Rate Plan (optional)</Label>
          <Select value={formData.rate_plan_id || 'all'} onValueChange={(v) => setFormData({ ...formData, rate_plan_id: v === 'all' ? '' : v })}>
            <SelectTrigger><SelectValue placeholder="All rate plans" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All rate plans</SelectItem>
              {ratePlans.map((rp) => (
                <SelectItem key={rp.id} value={rp.id}>{rp.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Start Date *</Label>
          <Input
            type="date"
            value={formData.start_date}
            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            required
          />
        </div>
        <div className="space-y-2">
          <Label>End Date *</Label>
          <Input
            type="date"
            value={formData.end_date}
            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
            required
          />
        </div>
      </div>

      {(formData.restriction_type === 'MIN_LOS' || formData.restriction_type === 'MAX_LOS') && (
        <div className="space-y-2">
          <Label>Value (nights) *</Label>
          <Input
            type="number"
            value={formData.value}
            onChange={(e) => setFormData({ ...formData, value: parseInt(e.target.value) })}
            min="1"
            required
          />
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" data-testid="restriction-submit-btn">Create Restriction</Button>
      </div>
    </form>
  );
}
